import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Checkbox,
  Tabs,
  DatePicker,
  message,
  Tooltip,
} from 'antd';
import {
  CalculatorOutlined,
  DiffOutlined,
  ImportOutlined,
  UnorderedListOutlined,
  DollarOutlined,
  SettingOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import Select from 'components/Select';
import Editor from 'components/Editor/index';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import PlainTransfer from 'components/PlainTransfer';
import { createDecimalRegExp } from 'utils/patterns';
import { onError } from 'utils/handlers';
import { isMutable } from 'utils/estadosGlobales';
import {
  getTiposDeCargos,
  postTipoDeCargo,
  patchTipoDeCargo,
  deleteTipoDeCargo,
  getTipoDeCargo,
} from 'api/recaudacion/tiposDeCargo';
import { getConceptoDeIngresos, getConceptosDeIngresos } from 'api/catalogos/conceptosDeIngresos';
import { getCatalogoDePadrones } from 'api/catalogos';
import { getPeriodos, getTiposDeAplicacionParaCargos } from 'api/recaudacion/index';
import {
  getMonedas,
  getTiposDeDato,
} from 'api/configuracion';
import { getCanalesDePago } from 'api/configuracion/canalDePago';
import { getTiposDeRecargos } from 'api/recaudacion/tiposDeRecargo';
import { getTiposDeActualizaciones } from 'api/recaudacion/tiposDeActualizacion';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import { getVariables } from 'api/configuracion/variables';
import { getCatalogoDeTarifas, getCatalogoDeTarifa } from 'api/configuracion/catalogoDeTarifas';
import { getTiposDeGastos } from 'api/recaudacion/tiposDeGasto';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import { getPeriodosFiscales } from 'api/catalogos/periodosFiscales';
import { capitalize } from 'utils/normalizers';
import { getClasificadoresDeTiposDeCargo } from 'api/recaudacion/clasificadorDeTiposDeCargo';
import { aplicacionesCargosEnum, periodosMap, tiposDatosEnum } from 'views/Tramites/GPM/enums';
import { getSistemas } from 'api/recaudacion/sistemas';
import { getClasesDeDeclaracionesEmpresariales } from 'api/empresas/claseDeClaracionesEmpresariales';
import Moment from 'moment';
import FormItemByMoneda, { monedasEnum } from 'components/TiposDeCargos/FormItemsByMoneda';
import FormItemsByApplicationType from 'components/TiposDeCargos/FormItemsByApplicationType';
import CalculoAproximado from 'components/TiposDeCargos/CalculoAproximado';
import TooltipTipoDeAplicacion, {
  TIPOS_DE_APLICACION_ACCESORIO_AUTODETERMINABLE,
  TIPOS_DE_APLICACION_AUTODETERMINABLE,
  TIPOS_DE_APLICACION_DETERMINABLE,
} from 'components/TooltipTipoDeAplicacion';
import ModalDuplicarTipoDeCargo from 'components/DuplicarTipoDeCargo';
import getAllPages from 'utils/getAllPages';

const { TabPane } = Tabs;
const baseURI = 'recaudacion/tipos-de-cargo/';
const NAVOJOA = process.env.REACT_APP_ENV_NAME === 'navojoa';

const catalogsModels = [
  'actualizacion',
  'catalogodecanalesdepago',
  'catalogodetarifa',
  'conceptodeingreso',
  'moneda',
  'padron',
  'periodo',
  'salariominimo',
  'tipodeaplicacionparacargo',
  'tipodedato',
  'tipodedescuento',
  'tipodegasto',
  'tipoderecargo',
  'uma',
  'variable',
];

const limits = {
  [periodosMap.DIARIO]: 365,
  [periodosMap.SEMANAL]: 52,
  [periodosMap.QUINCENAL]: 24,
  [periodosMap.MENSUAL]: 12,
  [periodosMap.BIMESTRAL]: 6,
  [periodosMap.TRIMESTRAL]: 4,
  [periodosMap.CUATRIMESTRAL]: 3,
  [periodosMap.SEMESTRAL]: 2,
  [periodosMap.ANUAL]: 1,
};

// eslint-disable-next-line react/prop-types
const TipoDeCargo = ({ permission }) => {
  const {
    estadosGlobales,
    padrones,
    tiposDeAplicacionCargos,
    tiposDeDato,
  } = useSelector((state) => state.catalogs);
  const monedaDefault = useSelector(({ auth }) => auth.entidad?.moneda);
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const [decimal] = createDecimalRegExp(monedaDefault);
  const [formGeneral] = Form.useForm();
  const [formCalculo] = Form.useForm();
  const [formAccesorios] = Form.useForm();
  const [formCanalesDePago] = Form.useForm();
  const [formVariables] = Form.useForm();
  const tipoDeAplicacion = Form.useWatch('tipo_de_aplicacion', formCalculo);
  const moneda = Form.useWatch('moneda', formCalculo);
  const hasClaseDeDeclaracion = Boolean(Form.useWatch('clase_de_declaracion_empresarial', formGeneral));
  const permitirImporteEnCero = Form.useWatch('permite_importe_en_cero', formGeneral);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [canalesDePago, setCanalesDePago] = useState([]);
  const [tiposDeRecargo, setTiposDeRecargo] = useState([]);
  const [tiposDeGasto, setTiposDeGasto] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [actualizaciones, setActualizaciones] = useState([]);
  const [tiposDescuentos, setTiposDescuentos] = useState([]);
  const [checkedValues, setCheckedValues] = useState(['solo_aplicar_mayor_descuento']);
  const [tarifas, setTarifas] = useState([]);
  const [accesorios, setAccesorios] = useState({ results: [] });
  const [activeTab, setActiveTab] = useState('general');
  const [periodos, setPeriodos] = useState([]);
  const [idRequired, setIdRequired] = useState(false);
  const [variables, setVariables] = useState([]);
  const [showAccesories, setShowAccesories] = useState(true);
  const [esDeterminable, setEsDeterminable] = useState(true);
  const [tarifa, setTarifa] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [sistemas, setSistemas] = useState([]);
  const [declaraciones, setDeclaraciones] = useState([]);

  const padronSelected = Form.useWatch('padron', formGeneral);
  const [mutable, setMutable] = useState(true);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [tiposDeCargoTemp, setTiposDeCargoTemp] = useState({ results: [] });
  const [tipoDeCargoDeclaracionTemp, setTipoDeCargoDeclaracionTemp] = useState({ results: [] });
  const [conceptosDeIngresoTemp, setConceptosDeIngresoTemp] = useState({ results: [] });
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [clasificadores, setClasificadores] = useState([]);
  const [filteredClasificadores, setFilteredClasificadores] = useState([]);
  const [conceptoSelected, setConceptoSelected] = useState(null);
  const [conceptosDeIngresoList, setConceptosDeIngresoList] = useState([]);

  const [visibleConfirmCopy, setVisibleConfirmCopy] = useState(false);

  const tiposDeCargo = useRef({ results: [] });
  const tiposDeCargoDeclaracion = useRef({ results: [] });
  const conceptosDeIngreso = useRef({ results: [] });

  const formInstances = [
    formGeneral,
    formCalculo,
    formCanalesDePago,
    formVariables,
  ];

  const fetchData = async (page = 1, q = null, params = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getTiposDeCargos({
      page,
      ...params,
      ...q,
    });
    setData(_data);
    setLoading(false);
  };

  const fetchAccesorios = async (pageAccesorios = 1, q = null, params = null) => {
    setLoading(true);
    const { padron } = formGeneral.getFieldsValue();
    const _accesorios = await getTiposDeCargos({
      page: pageAccesorios,
      padron,
      es_accesorio: true,
      descripcion: q,
      ...params,
    });
    if ((((accesorios.q && q) && accesorios.q === q)
    || (!accesorios.q && !q)) && padron) {
      setAccesorios((prev) => ({
        ..._accesorios,
        results: [...prev?.results, ..._accesorios.results],
        q,
      }));
    } else {
      setAccesorios({ ..._accesorios, q });
    }
    setLoading(false);
  };

  const onCancel = () => {
    setVisible(false);
    formAccesorios.resetFields();
    formCalculo.resetFields();
    formGeneral.resetFields();
    formVariables.resetFields();
    formCanalesDePago.resetFields();
    setSelectedRowKeys([]);
    setCheckedValues([]);
    setAccesorios({ results: [] });
    setShowAccesories(true);
    setActiveTab('general');
    setTiposDeCargoTemp({ results: [] });
    setTipoDeCargoDeclaracionTemp({ results: [] });
    setConceptosDeIngresoTemp({ results: [] });
    setTarifa();
    setMutable(true);
    setFilteredClasificadores([]);
    setIdRequired(false);
    setConceptoSelected(null);
  };

  useEffect(() => {
    const fetch = async () => {
      if (NAVOJOA) {
        const _conceptosDeIngresoList = await getAllPages(getConceptosDeIngresos);
        setConceptosDeIngresoList(_conceptosDeIngresoList);
      }
      await fetchData(1, null, periodoFiscalSelected?.id);
    };
    if (!loading) fetch();
    // eslint-disable-next-line
  }, [periodoFiscalSelected]);

  useEffect(() => {
    let mounted = true;
    let allPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        allPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };

    const fetchAll = async () => {
      const [
        dataMonedas,
        dataPeriodos,
        dataCanalesDePago,
        dataTiposDeRecargos,
        resTarifasCatalogo,
        dataActualizaciones,
        dataVariables,
        dataDescuentos,
        _periodosFiscales,
        _clasificadores,
        _sistemas,
        _declaraciones,
      ] = await Promise.all([
        getMonedas(),
        getPeriodos(),
        getCanalesDePago(),
        getTiposDeRecargos(),
        getCatalogoDeTarifas(),
        getTiposDeActualizaciones(),
        getVariables(),
        getTiposDeDescuentos({ aplicacion_del_descuento: 1 }),
        getPeriodosFiscales(),
        getClasificadoresDeTiposDeCargo(),
        getSistemas(),
        getClasesDeDeclaracionesEmpresariales(),
        getTiposDeDato(),
        getCatalogoDePadrones(),
        getTiposDeAplicacionParaCargos(),
      ]);
      if (NAVOJOA) {
        const _conceptosDeIngresoList = await getAllPages(getConceptosDeIngresos);
        setConceptosDeIngresoList(_conceptosDeIngresoList);
      }
      if (mounted) {
        const filteredTiposDeDescuentos = dataDescuentos.filter((d) => d.puede_aplicar)
          .map((e) => ({
            ...e,
            descripcion: e.es_descuento_especial
              ? `(${capitalize('Especial')}) ${e.descripcion}` : e.descripcion,
          }));
        setPeriodos(dataPeriodos);
        setVariables(dataVariables);
        setTarifas(resTarifasCatalogo);
        setTiposDescuentos(filteredTiposDeDescuentos);
        setCanalesDePago(dataCanalesDePago);
        setTiposDeRecargo(dataTiposDeRecargos);
        setActualizaciones(dataActualizaciones);
        setMonedas(dataMonedas.filter((e) => e.id === monedaDefault?.id || e.calculable));
        setPeriodosFiscales(_periodosFiscales);
        setClasificadores(_clasificadores.filter((e) => e.clasificador.includes('.')));
        setSistemas(_sistemas);
        setDeclaraciones(_declaraciones);
        await fetchData(1, null, { periodo_fiscal: periodoFiscalSelected.id });
        setLoading(false);
      }
    };
    validatePermission();

    if (allPermission) {
      fetchAll();
    }

    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const options = [
    {
      label: 'Convenio',
      value: 'es_convenio',
    },
    {
      label: 'Cargo Único',
      value: 'es_cargo_unico',
      disabled: esDeterminable,
      messageOnDisabled: 'Cuando la naturaleza del concepto de ingresos es determinable el cargo es único de forma obligatoria.',
    },
    {
      label: 'No Cargar Manualmente',
      value: 'no_cargar_manualmente',
    },
    {
      label: 'No Aplica para Descuento',
      value: 'no_aplica_descuento',
    },
    {
      label: 'Es un Accesorio',
      value: 'es_accesorio',
      disabled: esDeterminable,
      messageOnDisabled: 'Cuando la naturaleza del concepto de ingresos es determinable el cargo no puede ser accesorio.',
    },
    {
      label: 'Usar Vencimiento Principal',
      value: 'usar_vencimiento_principal',
      disabled: !checkedValues.includes('es_accesorio'),
      messageOnDisabled: 'Solo los cargos que son accesorios pueden usar esta opción.',
    },
    {
      label: 'Es Actualización',
      value: 'es_actualizacion',
      disabled: !checkedValues.includes('es_accesorio'),
      messageOnDisabled: 'Solo los cargos que son accesorios pueden usar esta opción.',
    },
    {
      label: 'Es Comisión Bancaria',
      value: 'es_comision_bancaria',
      disabled: !checkedValues.includes('es_accesorio'),
      messageOnDisabled: 'Solo los cargos que son accesorios pueden usar esta opción.',
    },
    {
      label: 'Es Requerible',
      value: 'es_cargo_no_requerible',
      disabled: !checkedValues.includes('es_accesorio'),
      messageOnDisabled: 'Solo los cargos que son accesorios pueden ser requeribles.',
    },
    {
      label: 'Es Facturable',
      value: 'es_facturable',
    },
    {
      label: 'Es Responsabilidad',
      value: 'es_responsabilidad',
    },
    {
      label: 'Es Declaración normal',
      value: 'es_declaracion_normal',
    },
    {
      label: 'Solo Aplicar Mayor Descuento',
      value: 'solo_aplica_el_descuento_mayor',
      disabled: checkedValues.includes('no_aplica_descuento'),
      messageOnDisabled: 'Solo los cargos que pueden tener descuentos pueden tener esta opción, si desea marcar esta opción desmarque la opción "No aplica para Descuento".',
    },
    {
      label: 'Fecha de vencimiento editable',
      value: 'es_editable_fecha_de_vencimiento',
    },
    {
      label: 'Es pago obligatorio',
      value: 'es_pago_obligatorio',
    },
  ];

  const autoCheckOnDeterminable = (_esDeterminable = false) => {
    setShowAccesories(_esDeterminable);
    if (_esDeterminable) {
      const clone = [...checkedValues];
      clone.push('es_cargo_unico');
      setCheckedValues(clone.filter((e) => e !== 'es_accesorio'
      && e !== 'es_cargo_no_requerible'));
    } else {
      setCheckedValues(checkedValues.filter((e) => e !== 'es_cargo_unico'));
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await formGeneral.validateFields();
      await formCalculo.validateFields();
      await formCanalesDePago.validateFields();
      await formVariables.validateFields();
      const [key] = selectedRowKeys;
      const values = {
        ...formGeneral.getFieldsValue(),
        ...formCalculo.getFieldsValue(),
        ...formAccesorios.getFieldsValue(),
        ...formCanalesDePago.getFieldsValue(),
        ...formVariables.getFieldsValue(),
        periodo_fiscal: periodoFiscalSelected?.id,
      };
      // insertando accesorios en 'values'
      options.forEach((item) => {
        values[`${item.value}`] = !!checkedValues.find((i) => i === item.value);
      });
      if (values.es_accesorio) {
        values.accesorios = [];
        values.tipos_de_cargo_adicional = [];
        values.tipos_de_gastos = [];
      } else {
        values.es_cargo_no_requerible = false;
      }
      if (tarifa?.tipo_de_tarifa === 4) {
        values.tipo_de_dato = null;
        values.parametro_descripcion = null;
      }
      if (values.no_aplica_descuento) {
        values.solo_aplica_el_descuento_mayor = false;
      }
      if (!esDeterminable) {
        values.tipo_de_cargo_padre = null;
      }
      if (values.periodo === 1) {
        values.identificador_periodo = 0;
      }
      const saved = await (key ? patchTipoDeCargo(key, values) : postTipoDeCargo(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances, setActiveTab);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDeCargo(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    formCalculo.setFieldsValue(record);
    setMutable(isMutable(record));
  };
  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const getTarifaOnSelect = async (id) => {
    setLoading(true);
    if (id) {
      const _tarifa = await getCatalogoDeTarifa(id);
      setTarifa(_tarifa);
    }
    setLoading(false);
  };

  const filterClasificadores = (padron) => {
    if (padron) {
      const subclasiffier = `${padron}`.length === 1 ? `.0${padron}` : `.${padron}`;
      const _clasificadores = clasificadores
        .filter((e) => e.clasificador.includes(subclasiffier));
      setFilteredClasificadores(_clasificadores);
    } else {
      setFilteredClasificadores([]);
    }
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _data = await getTipoDeCargo(key);
    const checked = options.filter((e) => _data[e.value]).map((e) => e.value);
    const _conceptodDeIngresos = await getConceptoDeIngresos(_data.concepto_de_ingreso);
    if (_conceptodDeIngresos) {
      setConceptoSelected(_conceptodDeIngresos);
    }

    if (_data?.tipo_de_cargo_padre) {
      const _tipoDeCargoPadre = await getTipoDeCargo(_data.tipo_de_cargo_padre);
      setTiposDeCargoTemp({ count: 1, results: [_tipoDeCargoPadre] });
    }
    if (_data?.tipo_de_cargo_superior_de_declaracion) {
      const _tipoDeCargoDeclaracion = await getTipoDeCargo(
        _data.tipo_de_cargo_superior_de_declaracion,
      );
      setTipoDeCargoDeclaracionTemp({ count: 1, results: [_tipoDeCargoDeclaracion] });
    }
    if (data?.periodo === 1) {
      setIdRequired(false);
    }
    const _esDeterminable = _conceptodDeIngresos.naturaleza_de_ingresos === 1;
    setConceptosDeIngresoTemp({ count: 1, results: [_conceptodDeIngresos] });
    setEsDeterminable(_esDeterminable);
    autoCheckOnDeterminable(_esDeterminable);
    await getTarifaOnSelect(_data.catalogo_de_tarifa);
    await fetchAccesorios(1, null, {
      es_accesorio: true,
      padron: _data.padron,
    });
    filterClasificadores(_data.padron);
    setCheckedValues(checked);
    setSelectedRowKeys([_data.id]);
    formGeneral.setFieldsValue(_data);
    formCalculo.setFieldsValue(_data);
    formAccesorios.setFieldsValue(_data);
    formCanalesDePago.setFieldsValue(_data);
    formVariables.setFieldsValue(_data);
    setShowAccesories(!_data.es_accesorio);
    const filteredDescuentos = _data.padron === 5 ? tiposDescuentos : tiposDescuentos
      .filter((e) => (e.padron === padronSelected) || e?.padron === _data.padron);
    setTiposDescuentos(filteredDescuentos);
    setLoading(false);
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    descripcion: [
      requiredRule,
      {
        validator: async (_, value) => {
          if (checkedValues.includes('es_facturable') && value?.length > 300) {
            throw new Error('La descripción no puede tener más de 300 caracteres');
          }
        },
      },
    ],
    importe: [
      {
        validator: async (rule, val) => {
          const { permite_importe_en_cero } = formGeneral.getFieldsValue();
          const notAllowZero = (typeof val === 'number' && !permite_importe_en_cero && val === 0);
          if ((typeof val === 'string' && !val?.length) || notAllowZero) {
            if (notAllowZero) {
              throw new Error('El importe no puede ser 0');
            }
            throw new Error('El campo es requerido');
          } else if (!decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          }
        },
      },
    ],
    importe_maximo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_maximo) {
            if (!decimal.test(values.importe_maximo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_maximo <= importe_minimo) {
              throw new Error('El campo debe ser mayor al Importe Mínimo');
            }
          }
        },
      },
    ],
    importe_minimo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_minimo) {
            if (!decimal.test(values.importe_minimo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_minimo >= importe_maximo) {
              throw new Error('El campo debe ser menor al Importe Máximo');
            }
          }
        },
      },
    ],
    canales_de_pago: [
      {
        validator: async (_, val) => {
          if (!val?.length) {
            throw new Error('Seleccione al menos un canal de pago');
          }
        },
      },
    ],
    identificador_periodo: [
      {
        validator: async () => {
          const { identificador_periodo, periodo } = formGeneral.getFieldsValue();
          if (periodo !== 1) {
            const idp = typeof identificador_periodo === 'string'
              ? parseInt(identificador_periodo, 10) : identificador_periodo;
            if (idp > limits[periodo]) {
              const desc = periodos.find((e) => e.id === periodo)?.descripcion;
              throw new Error(`El valor máximo es ${limits[periodo]} ya que el periodo es ${desc}`);
            }
          }
        },
      },
    ],
    requiredIfIsAccesorio: [
      { required: !checkedValues.includes('es_accesorio') },
    ],
  };

  const columns = [
    {
      titleText: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
    },
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Padrón',
      dataIndex: 'padron',
      key: 'padron',
      width: 150,
      catalog: {
        cat: padrones,
        labelProp: 'descripcion',
        use: true,
      },
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Estado Global',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
        use: true,
      },
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 500,
      render: (val) => (val || 'N/A'),
    },
    {
      titleText: 'Moneda',
      dataIndex: 'moneda',
      key: 'moneda',
      width: 300,
      catalog: {
        cat: monedas,
        labelProp: 'descripcion',
        use: true,
      },
    },
    {
      titleText: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 150,
      catalog: {
        cat: periodos,
        labelProp: 'periodo',
        use: true,
      },
    },
    {
      titleText: 'Sistema',
      dataIndex: 'sistemas',
      key: 'sistemas',
      width: 200,
      catalog: {
        cat: sistemas,
        labelProp: 'descripcion',
        use: true,
      },
    },
    {
      titleText: 'Tipo de Aplicación',
      dataIndex: 'tipo_de_aplicacion',
      key: 'tipo_de_aplicacion',
      width: 200,
      catalog: {
        cat: tiposDeAplicacionCargos,
        labelProp: 'descripcion',
        use: true,
      },
    },
    {
      titleText: 'Fecha V',
      dataIndex: 'fecha_de_vencimiento',
      key: 'fecha_de_vencimiento',
      width: 150,
      render: (val) => (val || 'N/A'),
    },
    {
      titleText: 'Id Periodo',
      dataIndex: 'identificador_periodo',
      key: 'identificador_periodo',
      width: 150,
      render: (val) => (val || 'N/A'),
    },
    {
      titleText: 'Es Cargo Único',
      dataIndex: 'es_cargo_unico',
      key: 'es_cargo_unico',
      width: 150,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'No Aplica para Descuento',
      dataIndex: 'no_aplica_descuento',
      key: 'no_aplica_descuento',
      width: 250,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Solo Aplicar Descuento Mayor',
      dataIndex: 'solo_aplica_descuento_mayor',
      key: 'solo_aplica_descuento_mayor',
      width: 250,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Es Accesorio',
      dataIndex: 'es_accesorio',
      key: 'es_accesorio',
      width: 150,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Es Actualización',
      dataIndex: 'es_actualizacion',
      key: 'es_actualizacion',
      width: 150,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Es Comisión Bancaria',
      dataIndex: 'es_comision_bancaria',
      key: 'es_comision_bancaria',
      width: 200,
      render: (val) => (val ? 'Sí' : 'No'),
    },
  ];

  const createClave = async (_, clave, filtered) => {
    try {
      setLoading(true);
      const next = parseInt(filtered[filtered.length - 1]?.clave.split('-').pop() || 0, 10) + 1;
      formGeneral.setFieldsValue({
        clave: `${clave}-${next}`,
      });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const getTiposDeAplicacion = () => tiposDeAplicacionCargos.filter((e) => {
    if (!esDeterminable && !showAccesories) {
      return TIPOS_DE_APLICACION_ACCESORIO_AUTODETERMINABLE.includes(e.id);
    }
    if (!esDeterminable) {
      return TIPOS_DE_APLICACION_AUTODETERMINABLE.includes(e.id);
    }
    return TIPOS_DE_APLICACION_DETERMINABLE.includes(e.id);
  });

  const onSelectConcepto = async (selected) => {
    try {
      if (selected) {
        setLoading(true);
        const _concepto = NAVOJOA ? conceptosDeIngresoList?.find((item) => item.id === selected)
          : conceptosDeIngreso.current?.results?.find((item) => item.id === selected);
        const {
          clave,
          descripcion,
          id,
          naturaleza_de_ingresos,
        } = _concepto;
        const claveDescripcion = descripcion.split(' ').map((e) => e[0]).join('');
        formGeneral.resetFields(['descripcion', 'clave']);
        const filtered = data.results.filter((e) => e.concepto_de_ingreso === id);
        const _esDeterminable = naturaleza_de_ingresos === 1;
        setEsDeterminable(_esDeterminable);
        autoCheckOnDeterminable(_esDeterminable);
        await createClave(id, `${clave}-${claveDescripcion.toLowerCase()}`, filtered);
        formGeneral.setFieldsValue({ descripcion });
        setConceptoSelected(_concepto);
        setLoading(false);
      } else {
        formGeneral.resetFields(['descripcion', 'clave']);
      }
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onSelectTipoDeAplicacion = (selected) => {
    if (selected !== aplicacionesCargosEnum.TARIFA) {
      formGeneral.setFieldsValue({
        tipo_de_dato: undefined,
        parametro_descripcion: undefined,
        catalogo_de_tarifa: undefined,
      });
    }
    if (selected === aplicacionesCargosEnum.MULTIPLO) {
      formCalculo.resetFields(['tipo_de_dato']);
      formCalculo.setFieldsValue({ tipo_de_dato: tiposDatosEnum.DECIMAL });
    }
  };

  const filterConceptosDeIngresos = async (padron) => {
    try {
      setLoading(true);
      formGeneral.resetFields(['concepto_de_ingreso', 'clave', 'descripcion']);
      formCalculo.setFieldsValue({ padron });
      if (padron && periodoFiscalSelected?.id) {
        await fetchAccesorios(1, null, { padron });
        const resultCargos = await getTiposDeCargos({ padron });
        const filteredDescuentos = padron === 5 ? tiposDescuentos : tiposDescuentos
          .filter((e) => (e.padron === padron) || e?.padron === 5);
        setTiposDeCargoTemp(resultCargos);
        setTipoDeCargoDeclaracionTemp(resultCargos);
        setTiposDescuentos(filteredDescuentos);
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const onClickCopy = async () => {
    setVisibleConfirmCopy(true);
  };

  useEffect(() => {
    const fetchGastos = async () => {
      const _gastos = await getTiposDeGastos();
      const gastos = padronSelected === 5 ? _gastos
        : _gastos.filter((e) => e.padron === padronSelected);
      setTiposDeGasto(gastos);
    };
    fetchGastos();
  }, [padronSelected, selectedRowKeys.length]);

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data.results}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            baseURI={baseURI}
            allowImport
            customActions={[
              {
                text: 'Duplicar',
                onClick: onClickCopy,
                hasPermission: hasActionPermission('tipos_de_cargo', ActionsNames.create),
                icon: <CopyOutlined />,
              },
            ]}
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
            pagination={false}
            paginationCallback={fetchData}
            count={data.count}
            currentPage={currentPage}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Cargo`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
                allowAuthorize
                selectedRowKeys={selectedRowKeys}
                baseURI={baseURI}
                callback={(id) => {
                  const [key] = selectedRowKeys;
                  const normalized = data.map((e) => {
                    if (e.id === key) {
                      const match = estadosGlobales.find((eg) => eg.id === id);
                      formGeneral.setFieldsValue({ estados_globales: match.id });
                      setMutable(true);
                      return { ...e, estados_globales: id };
                    }
                    return e;
                  });
                  setData(normalized);
                }}
              />
            )}
            bordered={false}
          >
            <Tabs
              type="card"
              onChange={setActiveTab}
              activeKey={activeTab}
            >
              <TabPane
                tab={(
                  <span>
                    <ImportOutlined />
                    General
                  </span>
                )}
                key="general"
                forceRender
              >
                <Form
                  layout="vertical"
                  form={formGeneral}
                  onFinish={() => onFinish()}
                  name="general"
                  initialValues={{
                    estados_globales: 1,
                    imprimir_observaciones: false,
                    permite_importe_en_cero: false,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="padron"
                        label="Padrón"
                        hasFeedback
                      >
                        <Select
                          onChange={(value) => {
                            filterConceptosDeIngresos(value);
                            filterClasificadores(value);
                          }}
                          dataSource={padrones}
                          disabled={!!selectedRowKeys.length || !mutable}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="concepto_de_ingreso"
                        label="Concepto de ingreso"
                        hasFeedback
                      >
                        {NAVOJOA ? (
                          <Select
                            dataSource={padronSelected === 5 ? conceptosDeIngresoList
                              : conceptosDeIngresoList
                                .filter((e) => e.padron === padronSelected)}
                            onClear={() => formGeneral.resetFields(['concepto_de_ingreso'])}
                            onChange={onSelectConcepto}
                            disabled={!!selectedRowKeys.length || !mutable || !padronSelected}
                          />
                        ) : (
                          <Select
                            dataSource={conceptosDeIngresoTemp.results?.length
                              ? conceptosDeIngresoTemp : conceptosDeIngreso.current}
                            onChange={onSelectConcepto}
                            disabled={!!selectedRowKeys.length || !mutable || !padronSelected}
                            usingExternalDataSource={!!conceptosDeIngresoTemp.results?.length}
                            onClear={() => formGeneral.resetFields(['concepto_de_ingreso'])}
                            pagination={{
                              getter: getConceptosDeIngresos,
                              setter: (dataSource) => {
                                conceptosDeIngreso.current = dataSource;
                              },
                              config: { qName: 'descripcion' },
                              params: padronSelected !== 5 ? { padron: padronSelected } : {},
                            }}
                          />
                        )}
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="clave"
                        label="Claves"
                        hasFeedback
                        normalize={capitalize}
                      >
                        <Input allowClear disabled={!!selectedRowKeys.length || !mutable} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                    >
                      <Form.Item
                        rules={rules.descripcion}
                        name="descripcion"
                        label="Descripción"
                        hasFeedback
                        normalize={capitalize}
                      >
                        <Input.TextArea
                          allowClear
                          autoSize={{ minRows: 3, maxRows: 5 }}
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                    >
                      <Form.Item
                        name="observaciones"
                        label="Observaciones"
                      >
                        <Input.TextArea
                          allowClear
                          autoSize={{ minRows: 3, maxRows: 5 }}
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="imprimir_observaciones"
                        label="¿Imprimir Observaciones?"
                        hasFeedback
                      >
                        <Select
                          trueFalse
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="fecha_de_vencimiento"
                        label="Fecha de vencimiento"
                        hasFeedback
                      >
                        <DatePicker
                          placeholder=""
                          format="DD/MM/YYYY"
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="clasificador_de_tipo_de_cargo_en_portal"
                        label="Clasificador"
                        hasFeedback
                      >
                        <Select
                          dataSource={filteredClasificadores}
                          disabled={!mutable}
                          labelProp="nombre"
                          keyProp="clasificador"
                          keyLabelRender
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="sistemas"
                        label="Sistema"
                        hasFeedback
                        rules={rules.requiredIfIsAccesorio}
                      >
                        <Select
                          dataSource={sistemas}
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="clase_de_declaracion_empresarial"
                        label="Clase de Declaración Empresarial"
                        hasFeedback
                      >
                        <Select
                          dataSource={declaraciones}
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="periodo"
                        label="Periodo"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          onChange={(val) => {
                            const notEventual = val ? val !== 1 : false;
                            setIdRequired(notEventual);
                            if (notEventual) {
                              formGeneral.validateFields(['identificador_periodo']);
                            } else {
                              formGeneral.resetFields(['identificador_periodo']);
                            }
                          }}
                          dataSource={periodos}
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                    {formGeneral.getFieldValue('periodo') !== 1 && (
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <NumericInput
                        rules={rules.identificador_periodo}
                        name="identificador_periodo"
                        label="Identificador del periodo"
                        required={idRequired}
                        disabled={!mutable}
                      />
                    </Col>
                    )}
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                    >
                      <Form.Item
                        name="tipo_de_cargo_superior_de_declaracion"
                        label="Tipo de cargo Superior de Declaración"
                        hasFeedback
                      >
                        <Select
                          disabled={!mutable}
                          dataSource={tipoDeCargoDeclaracionTemp.results?.length
                            ? tipoDeCargoDeclaracionTemp : tiposDeCargoDeclaracion.current}
                          usingExternalDataSource={Boolean(tipoDeCargoDeclaracionTemp
                            .results?.length)}
                          labelProp="descripcion"
                          render={(record) => `${periodosFiscales
                            .find((e) => e.id === record.periodo_fiscal)?.periodo} - ${record.descripcion}`}
                          onClear={() => {
                            formGeneral.resetFields(['tipo_de_cargo_superior_de_declaracion']);
                            setTipoDeCargoDeclaracionTemp({ results: [] });
                          }}
                          pagination={{
                            getter: (params) => getTiposDeCargos(params, false),
                            setter: (dataSource) => {
                              tiposDeCargoDeclaracion.current = dataSource;
                              setTipoDeCargoDeclaracionTemp({ results: [] });
                            },
                            config: { auto: true, qName: 'descripcion' },
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="permite_importe_en_cero"
                        label="¿Permite Importe en Cero?"
                        hasFeedback
                      >
                        <Select
                          trueFalse
                          disabled={!mutable}
                          onChange={async () => {
                            const field = ['importe'];
                            formCalculo.resetFields(field);
                            await formCalculo.validateFields(field);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          disabled={!selectedRowKeys.length || !mutable}
                          dataSource={estadosGlobales}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <UnorderedListOutlined />
                    Reglas
                  </span>
                )}
                key="reglas"
                forceRender
              >
                <Row gutter={10} style={{ margin: 0 }}>
                  <Checkbox.Group
                    style={{ width: '100%' }}
                    onChange={async (values) => {
                      let _checkedValues = [...values];
                      if (!_checkedValues.includes('es_accesorio')
                      && checkedValues.includes('es_cargo_no_requerible')) {
                        _checkedValues = _checkedValues
                          .filter((e) => e !== 'es_cargo_no_requerible');
                      }
                      if (_checkedValues.includes('no_aplica_descuento')
                      && checkedValues.includes('solo_aplica_el_descuento_mayor')) {
                        _checkedValues = _checkedValues
                          .filter((e) => e !== 'solo_aplica_el_descuento_mayor');
                      }
                      if (_checkedValues.includes('es_facturable')) {
                        const descripcion = formGeneral.getFieldValue('descripcion');
                        message.warn('Habilitar esta regla limitará el campo "Descripción" a 300 caracteres.');
                        const _descripcion = descripcion.slice(0, 300);
                        formGeneral.setFieldsValue({ descripcion: _descripcion });
                        await formGeneral.validateFields(['descripcion']);
                      }
                      const _accesorio = values.includes('es_accesorio');
                      setShowAccesories(!_accesorio);
                      setCheckedValues(_checkedValues);
                      if (_accesorio) {
                        const { tipo_de_aplicacion, moneda: _moneda, padron } = formCalculo
                          .getFieldsValue();
                        if (!tiposDeAplicacionCargos.filter((_, idx) => (idx + 1) % 2 === 0)
                          .some((e) => e.id === tipo_de_aplicacion)) {
                          onSelectTipoDeAplicacion(tipo_de_aplicacion);
                          formCalculo.resetFields();
                          formCalculo.setFieldsValue({ moneda: _moneda, padron });
                        }
                      }
                    }}
                    value={checkedValues}
                    disabled={!mutable}
                  >
                    <Row>
                      {options.map((item) => {
                        const {
                          label,
                          value,
                          disabled,
                          messageOnDisabled,
                        } = item;
                        return (
                          <Col xs={24} sm={24} md={8} key={value}>
                            {!disabled ? (
                              <Checkbox disabled={!mutable} value={value}>
                                {label}
                              </Checkbox>
                            ) : (
                              <Tooltip title={messageOnDisabled}>
                                <Checkbox disabled value={value}>
                                  {label}
                                </Checkbox>
                              </Tooltip>
                            )}
                          </Col>
                        );
                      })}
                    </Row>
                  </Checkbox.Group>
                </Row>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <CalculatorOutlined />
                    Parámetros de Cálculo
                  </span>
                )}
                key="calculo"
                forceRender
              >
                <Form
                  layout="vertical"
                  form={formCalculo}
                  onFinish={() => onFinish()}
                  name="calculo"
                  initialValues={{
                    moneda: monedaDefault?.id,
                    usar_tipo_de_cambio_actual_por_moneda: new Moment()
                      .format('YYYY') === `${periodoFiscalSelected.periodo}`,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="moneda"
                        label="Moneda"
                        hasFeedback
                      >
                        <Select
                          dataSource={monedas}
                          keyLabelRender
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="padron"
                        label="Padrón"
                        hasFeedback
                      >
                        <Select disabled dataSource={padrones} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="tipo_de_aplicacion"
                        label="Tipo de aplicación"
                        hasFeedback
                        tooltip={(
                          <TooltipTipoDeAplicacion
                            concepto={conceptoSelected}
                            esAccesorio={!showAccesories}
                          />
                        )}
                      >
                        <Select
                          onChange={onSelectTipoDeAplicacion}
                          dataSource={getTiposDeAplicacion()}
                          disabled={!mutable}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {(monedasEnum.PESO !== moneda
                  || (tipoDeAplicacion && aplicacionesCargosEnum.FIJO !== tipoDeAplicacion)) && (
                    <Row gutter={10}>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="usar_tipo_de_cambio_actual_por_moneda"
                          label="¿Calcular con tipo de cambio actual?"
                          hasFeedback
                        >
                          {/*
                            El campo estará deshabilitado si se esta creando en el periodo actual
                           */}
                          <Select
                            trueFalse
                            disabled={!mutable
                              || !selectedRowKeys.length
                              || new Moment()
                                .format('YYYY') === `${periodoFiscalSelected.periodo}`}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                  <Row gutter={10}>
                    <Col span={24}>
                      {(tipoDeAplicacion === aplicacionesCargosEnum.TARIFA) && (
                        <Row gutter={10}>
                          <Col
                            xs={24}
                            sm={24}
                            md={8}
                          >
                            <Form.Item
                              name="catalogo_de_tarifa"
                              rules={rules.required}
                              label="Tarifa"
                              hasFeedback
                            >
                              <Select
                                dataSource={tarifas}
                                onChange={getTarifaOnSelect}
                                disabled={!mutable}
                              />
                            </Form.Item>
                          </Col>
                          {tarifa?.tipo_de_tarifa !== 4 && (
                            <>
                              <Col
                                xs={24}
                                sm={24}
                                md={8}
                              >
                                <Form.Item
                                  name="tipo_de_dato"
                                  label="Tipo de dato"
                                  rules={rules.required}
                                  hasFeedback
                                >
                                  <Select
                                    dataSource={tiposDeDato.filter((e) => e.es_para_formula)}
                                    disabled={!mutable}
                                  />
                                </Form.Item>
                              </Col>
                              <Col
                                xs={24}
                                sm={24}
                                md={8}
                              >
                                <Form.Item
                                  name="parametro_descripcion"
                                  label="Descripción"
                                  rules={rules.required}
                                  hasFeedback
                                >
                                  <Input allowClear disabled={!mutable} />
                                </Form.Item>
                              </Col>
                            </>
                          )}
                        </Row>
                      )}
                    </Col>
                    <FormItemsByApplicationType
                      tipoDeAplicacion={tipoDeAplicacion}
                      moneda={moneda}
                      mutable={mutable}
                      allowZero={permitirImporteEnCero}
                    />
                    {/* Formula */}
                    {(tipoDeAplicacion === aplicacionesCargosEnum.FORMULA) && (
                      <Editor
                        form={formCalculo}
                        rules={rules.required}
                        cargo
                        variables={variables}
                        disabled={!mutable}
                      />
                    )}
                    {(tipoDeAplicacion === aplicacionesCargosEnum.MULTIPLO) && (
                      <>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="tipo_de_dato"
                            label="Tipo de dato"
                            rules={rules.required}
                            hasFeedback
                          >
                            <Select dataSource={tiposDeDato} disabled />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="parametro_descripcion"
                            label="Descripción"
                            rules={rules.required}
                            hasFeedback
                          >
                            <Input allowClear disabled={!mutable} />
                          </Form.Item>
                        </Col>
                        <FormItemByMoneda
                          moneda={moneda}
                          mutable={mutable}
                          allowZero={permitirImporteEnCero}
                        />
                      </>
                    )}
                    {(tipoDeAplicacion === aplicacionesCargosEnum.PORCENTAJE) && (
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          required
                          name="importe"
                          label="Porcentaje (%)"
                          disabled={!mutable}
                          decimal
                        />
                      </Col>
                    )}
                    {(tipoDeAplicacion === aplicacionesCargosEnum.SQL_FUNCTION
                    && hasClaseDeDeclaracion) && (
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <NumericInput
                        required
                        name="importe"
                        label="Porcentaje (%)"
                        disabled={!mutable}
                        decimal
                      />
                    </Col>
                    )}
                    {(tipoDeAplicacion === aplicacionesCargosEnum.SQL_FUNCTION) && (
                      <Editor
                        form={formCalculo}
                        rules={rules.required}
                        cargo
                        variables={variables}
                        disabled={!mutable}
                      />
                    )}
                  </Row>
                  <CalculoAproximado setLoading={setLoading} />
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <DiffOutlined />
                    Accesorios
                  </span>
                )}
                key="accesorios"
              >
                <Form
                  layout="vertical"
                  form={formAccesorios}
                  onFinish={() => onFinish()}
                  name="accesorios"
                >
                  {(esDeterminable) && (
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                    >
                      <Form.Item
                        name="tipo_de_cargo_padre"
                        label="Tipo de cargo adicional"
                        hasFeedback
                      >
                        <Select
                          disabled={!mutable}
                          dataSource={tiposDeCargoTemp?.results?.length
                            ? tiposDeCargoTemp : tiposDeCargo.current}
                          usingExternalDataSource={Boolean(tiposDeCargoTemp.results?.length)}
                          labelProp="descripcion"
                          render={(record) => `${periodosFiscales
                            .find((e) => e.id === record.periodo_fiscal)?.periodo} - ${record.descripcion}`}
                          onClear={() => {
                            formAccesorios.resetFields(['tipo_de_cargo_padre']);
                            setTiposDeCargoTemp({ results: [] });
                          }}
                          pagination={{
                            getter: (params) => getTiposDeCargos(params, false),
                            setter: (dataSource) => {
                              tiposDeCargo.current = dataSource;
                              setTiposDeCargoTemp({ results: [] });
                            },
                            config: { auto: true, qName: 'descripcion' },
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {showAccesories && (
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={tiposDeGasto}
                        label="Tipo de gastos"
                        formItemName="tipos_de_gasto"
                        form={formAccesorios}
                        filterProp="descripcion"
                        disabled={!mutable}
                      />
                    </Col>
                  )}
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={tiposDeRecargo}
                      label="Tipo de recargo"
                      formItemName="tipos_de_recargo"
                      form={formAccesorios}
                      filterProp="descripcion"
                      disabled={!mutable}
                    />
                  </Col>
                  {showAccesories && (
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={accesorios}
                        label="Accesorios"
                        formItemName="accesorios"
                        form={formAccesorios}
                        filterProp="descripcion"
                        disabled={!mutable || !formGeneral.getFieldValue('padron')}
                        callbackPagination={fetchAccesorios}
                        pagination={false}
                      />
                    </Col>
                  )}
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={actualizaciones}
                      label="Tipo de actualización"
                      formItemName="tipos_de_actualizacion"
                      form={formAccesorios}
                      filterProp="descripcion"
                      disabled={!mutable}
                    />
                  </Col>
                  {!checkedValues.includes('no_aplica_descuento') && (
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={tiposDescuentos}
                        label="Tipo de descuento"
                        formItemName="tipos_de_descuento"
                        form={formAccesorios}
                        filterProp="descripcion"
                        disabled={!mutable}
                      />
                    </Col>
                  )}
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <DollarOutlined />
                    Canales de Pago
                  </span>
                )}
                key="canalesDePago"
                forceRender
              >
                <Form
                  form={formCanalesDePago}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  name="canalesDePago"
                >
                  <Form.Item
                    label=""
                    name="canales_de_pago"
                    rules={rules.canales_de_pago}
                  >
                    <Checkbox.Group style={{ width: '100%' }}>
                      <Row gutter={[10, 5]}>
                        {canalesDePago.map((item) => (
                          <Col
                            xs={24}
                            sm={24}
                            md={8}
                            key={item.id}
                          >
                            <Checkbox disabled={!mutable} value={item.id}>
                              {`${item.clave} - ${item.descripcion}`}
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Variables
                  </span>
                )}
                key="variables"
                forceRender
                disabled={tipoDeAplicacion !== aplicacionesCargosEnum.FORMULA || !showAccesories}
              >
                <Form
                  form={formVariables}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  name="variables"
                >
                  <Row>
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={variables}
                        label="Variables"
                        formItemName="variables"
                        form={formVariables}
                        filterProp="descripcion_de_variable"
                        disabled={!mutable}
                      />
                    </Col>
                  </Row>
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Tipo de Cargo ${data.results.find((e) => e.id === selectedRowKeys[0])?.descripcion}`}
          loading={loading}
        />
        <ModalDuplicarTipoDeCargo
          visible={visibleConfirmCopy}
          setVisible={setVisibleConfirmCopy}
          tipoDeCargo={data.results.find((e) => e.id === selectedRowKeys[0])}
          loading={loading}
          setLoading={setLoading}
          periodosFiscales={periodosFiscales}
          callback={async () => {
            await fetchData();
          }}
        />
      </Spin>
    </Row>
  );
};

export default TipoDeCargo;
